import { Radio } from "@mui/material";
import { useState } from "react";
import ICONS from "../../../assets/Icons/index.js";
import globalText from "../../../utils/globalText.js";
import { globalTextFormat } from "../../../utils/globalTextFormat.js";
import AmountInputField from "../CommonInputFields/AmountInputField";
import FormatAmount from "../FormatAmount/FormatAmount.js";
import { GlobalConfiguration } from "../../../utils/configurationProvider.js";
/**
 * @typedef {Object} CopyData
 * @property {string} title - Title of the data to copy (e.g., "Transfer Amount")
 * @property {string} data_type - Type of data (e.g., "numeric")
 * @property {string} icon - Icon to show for the paste action
 * @property {string|number} value - The value to be pasted
 */

/**
 * @typedef {Object} TxnPayLoad
 * @property {string|number} amount - The transaction amount
 * @property {string} amountWords - The amount converted to words
 */

/**
 * @typedef {Object} AmountProps
 * @property {string} title - Section title (default: "Amount")
 * @property {string|number} amount - Current entered amount (default: "")
 * @property {string} amountInWords - Amount converted to words (default: "")
 *
 * @property {CopyData} copyData - Details for paste functionality (default: {})
 * @property {Function} onAmountChange - Called on amount change (default: () => {})
 * @property {Function} onPasteApplied - Called after paste action (default: () => {})
 *
 * @property {boolean} [isPreview=false] - Shows static summary card
 * @property {TxnPayLoad} txnPayLoad - Payload used in preview mode (default: {})
 * @property {Function} onAmountExceedsRequested - Called when the entered amount exceeds the requested amount.
 */

const Amount = ({
  title,
  amount,
  amountInWords,
  onAmountChange,
  isPreview = false,
  txnPayLoad,
  selectedRadioValue,
  NewAmount,
  handleDivBtn,
  customAmountRequired = false,
  flow,
  sourceAmount,
  destinationAmount,
  copyData,
  onPasteApplied,
  onAmountExceedsRequested,
  requestAmountRequired = false,
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const pasteAvailable = copyData?.data_type === "numeric";
  const computeVariant = (value, focused) => {
    const entered = Number(value || 0);
    const requested = Number(NewAmount || 0);

    if (!customAmountRequired || flow === "internlaTxnCustom") return "default";

    if (focused) {
      return entered > 0 && entered <= requested ? "typing" : "typing";
    } else {
      if (!entered) return "default";
      if (entered > requested) return "error";
      return "default";
    }
  };

  if (isPreview) {
    return (
      <div className="w-width-full rounded-border-radius-md bg-primary-card h-auto">
        <div className="flex flex-col bg-positive-bg justify-center p-padding-md rounded-border-radius-md text-font-xl text-text-primary">
          <p className="font-semibold text-font-md mb-margin-md">
            {globalText?.AMOUNT}
          </p>

          <div className="flex text-font-xl">
            <span>INR </span>
            <p className="ml-margin-sm text-text-primary">
              <FormatAmount price={txnPayLoad?.amount} />
            </p>
          </div>

          <p className="text-text-secondary text-font-xs font-medium mt-margin-xs">
            {globalText?.AMOUNT}
          </p>

          <div className="border border-dashed border-text-secondary w-width-full my-margin-sm" />

          <div className="text-sm text-left">
            <p className="capitalize mb-margin-xs">
              {txnPayLoad?.amountInWords}
            </p>
            <p className="text-text-secondary text-font-xs">
              {globalText?.AMOUNT_IN_WORDS}
            </p>
          </div>
        </div>
        {requestAmountRequired && (
          <>
            <div className="bg-primary-card py-padding-sm px-padding-md rounded-bl-border-radius-md rounded-br-border-radius-md">
              <p
                className={`${ Number(txnPayLoad?.amount) !== Number(txnPayLoad?.requestedAmount) ? "text-queued" : "text-text-primary"} text-font-md  font-semibold`}
              >
                INR{" "}
                {<FormatAmount price={txnPayLoad?.requestedAmount} /> || "NA"}
              </p>
              <p className="text-text-secondary text-font-xs mt-margin-xs">
                Requested amount
              </p>
            </div>
          </>
        )}
      </div>
    );
  }

  return (
    <div className="w-width-full max-h-auto">
      <div className="w-width-full h-fit">
        <div className="flex flex-col relative">
          <div className="p-padding-md h-height-full bg-positive-bg flex flex-col rounded-border-radius-md">
            <div className="pb-padding-md">
              <p className="text-text-primary text-font-md font-semibold">
                {globalTextFormat(title, [0, 1])}
              </p>
            </div>
            <AmountInputField
              upperLimit={GlobalConfiguration.maxAllowedAmount}
              name="amount"
              value={amount}
              placeholder="Amount"
              bgColor="var(--positive-bg)"
              fgColor="var(--text-primary)"
              isFullWidth={true}
              isDisabled={false}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              adornment={pasteAvailable ? ICONS?.PASTE_WHITE : undefined}
              onInput={(val) => onAmountChange(val)}
              onAdornmentClick={
                pasteAvailable
                  ? () => {
                      onAmountChange(copyData?.value?.toString?.() || "");
                      onPasteApplied();
                    }
                  : onAmountExceedsRequested
              }
              variant={
                pasteAvailable ? "default" : computeVariant(amount, isFocused)
              }
            />

            {amount && amountInWords && (
              <div className="animate-slideDown text-text-primary text-sm w-width-full flex flex-col mt-margin-sm">
                <div className="capitalize">{amountInWords}</div>
                <p className="text-text-secondary text-font-xs mt-margin-xs">
                  {globalText?.IN_WORDS}
                </p>
              </div>
            )}

            {customAmountRequired && (
              <>
                <div
                  className={`flex flex-row items-center w-width-full mt-margin-md rounded-border-radius-md p-padding-sm mobile-account-name-div ${
                    selectedRadioValue === "customname"
                      ? "bg-positive-act cursor-default"
                      : "bg-positive-card cursor-pointer global-hover-white relative z-z-index-base"
                  }`}
                  onClick={() => {
                    handleDivBtn("customname");
                  }}
                >
                  <Radio
                    checked={selectedRadioValue === "customname"}
                    onChange={() => handleDivBtn("customname")}
                    value="customname"
                    name="radio-buttons"
                    inputProps={{ "aria-label": "B" }}
                    sx={{
                      "& .MuiSvgIcon-root": {
                        width: "var(--icon-md)",
                        height: "var(--icon-md)",
                      },
                      color:
                        selectedRadioValue === "customname"
                          ? "var(--text-primary)"
                          : "var(--text-primary)",
                      "&.Mui-checked": {
                        color:
                          selectedRadioValue === "customname"
                            ? "var(--text-primary)"
                            : "var(--text-primary)",
                      },
                      padding: "var(--padding-base) var(--padding-sm)",
                    }}
                  />
                  <div className="flex flex-col">
                    <div
                      className={`text-font-sm font-semibold truncate ${
                        selectedRadioValue === "customname"
                          ? "text-text-primary"
                          : "text-text-primary"
                      }`}
                    >
                      Custom amount
                    </div>
                    <p className="text-font-xs font-normal truncate text-text-primary">
                      Enter custom amount
                    </p>
                  </div>
                </div>
                <div className=" my-margin-sm flex justify-center">
                  <p className=" text-text-primary text-font-xs font-semibold">
                    {flow === "internlaTxnCustom"
                      ? "Or select below to autofill the suggested amount"
                      : "Or select below to autofill the requested amount"}
                  </p>
                </div>
                {flow === "internlaTxnCustom" ? (
                  <div className="flex flex-col gap-y-gap-sm">
                    {sourceAmount ? (
                      <div
                        className={`flex flex-row items-center w-width-full rounded-border-radius-md p-padding-sm mobile-account-name-div  ${
                          selectedRadioValue === "sourceAcc"
                            ? "bg-positive-act cursor-default"
                            : "bg-positive-card cursor-pointer global-hover-white relative z-z-index-base"
                        }`}
                        onClick={() => {
                          handleDivBtn("sourceAcc");
                        }}
                      >
                        <Radio
                          checked={selectedRadioValue === "sourceAcc"}
                          onChange={() => handleDivBtn("sourceAcc")}
                          value="sourceAcc"
                          name="radio-buttons"
                          inputProps={{ "aria-label": "B" }}
                          sx={{
                            "& .MuiSvgIcon-root": {
                              width: "var(--icon-md)",
                              height: "var(--icon-md)",
                            },
                            color:
                              selectedRadioValue === "sourceAcc"
                                ? "var(--text-primary)"
                                : "var(--text-primary)",
                            "&.Mui-checked": {
                              color:
                                selectedRadioValue === "sourceAcc"
                                  ? "var(--text-primary)"
                                  : "var(--text-primary)",
                            },
                            padding: "var(--padding-base) var(--padding-sm)",
                          }}
                        />
                        <div className="flex flex-col">
                          <div
                            className={`text-font-sm font-semibold truncate ${
                              selectedRadioValue === "sourceAcc"
                                ? "text-text-primary"
                                : "text-text-primary"
                            }`}
                          >
                            <div className="flex">
                              <span>INR</span>
                              <p className="ml-margin-xs ">
                                <FormatAmount price={sourceAmount} />
                              </p>
                            </div>
                          </div>
                          <p
                            className={`text-font-xs font-normal truncate text-text-primary`}
                          >
                            Autofill source balance
                          </p>
                        </div>
                      </div>
                    ) : null}
                    {destinationAmount ? (
                      <div
                        className={`flex flex-row items-center w-width-full rounded-border-radius-md p-padding-sm mobile-account-name-div  ${
                          selectedRadioValue === "destinationAcc"
                            ? "bg-positive-act cursor-default"
                            : "bg-positive-card cursor-pointer global-hover-white relative z-z-index-base"
                        }`}
                        onClick={() => {
                          handleDivBtn("destinationAcc");
                        }}
                      >
                        <Radio
                          checked={selectedRadioValue === "destinationAcc"}
                          onChange={() => handleDivBtn("destinationAcc")}
                          value="destinationAcc"
                          name="radio-buttons"
                          inputProps={{ "aria-label": "B" }}
                          sx={{
                            "& .MuiSvgIcon-root": {
                              width: "var(--icon-md)",
                              height: "var(--icon-md)",
                            },
                            color:
                              selectedRadioValue === "destinationAcc"
                                ? "var(--text-primary)"
                                : "var(--text-primary)",
                            "&.Mui-checked": {
                              color:
                                selectedRadioValue === "destinationAcc"
                                  ? "var(--text-primary)"
                                  : "var(--text-primary)",
                            },
                            padding: "var(--padding-base) var(--padding-sm)",
                          }}
                        />
                        <div className="flex flex-col">
                          <div
                            className={`text-font-sm font-semibold truncate ${
                              selectedRadioValue === "destinationAcc"
                                ? "text-text-primary"
                                : "text-text-primary"
                            }`}
                          >
                            <div className="flex">
                              <span>INR</span>
                              <p className="ml-margin-xs ">
                                <FormatAmount price={destinationAmount} />
                              </p>
                            </div>
                          </div>
                          <p className="text-font-xs font-normal truncate text-text-primary">
                            Autofill destination balance
                          </p>
                        </div>
                      </div>
                    ) : null}
                  </div>
                ) : (
                  <div
                    className={`flex flex-row items-center w-width-full rounded-border-radius-md p-padding-sm mobile-account-name-div  ${
                      selectedRadioValue === "username"
                        ? "bg-positive-act cursor-default"
                        : "bg-positive-card cursor-pointer global-hover-white relative z-z-index-base"
                    }`}
                    onClick={() => {
                      handleDivBtn("username");
                    }}
                  >
                    <Radio
                      checked={selectedRadioValue === "username"}
                      onChange={() => handleDivBtn("username")}
                      value="username"
                      name="radio-buttons"
                      inputProps={{ "aria-label": "B" }}
                      sx={{
                        "& .MuiSvgIcon-root": {
                          width: "var(--icon-md)",
                          height: "var(--icon-md)",
                        },
                        color:
                          selectedRadioValue === "username"
                            ? "var(--text-primary)"
                            : "var(--text-primary)",
                        "&.Mui-checked": {
                          color:
                            selectedRadioValue === "username"
                              ? "var(--text-primary)"
                              : "var(--text-primary)",
                        },
                        padding: "var(--padding-base) var(--padding-sm)",
                      }}
                    />
                    <div className="flex flex-col">
                      <div
                        className={`text-font-sm font-semibold truncate ${
                          selectedRadioValue === "username"
                            ? "text-text-primary"
                            : "text-text-primary"
                        }`}
                      >
                        <div className="flex ">
                          <span>INR</span>
                          <p className="ml-margin-xs ">
                            <FormatAmount price={NewAmount} />
                          </p>
                        </div>
                      </div>
                      <p
                        className={`text-font-xs font-normal truncate text-text-primary`}
                      >
                        Autofill requested amount
                      </p>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Amount;
